import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import Paginator from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { useDebounce } from "../../hooks/common";
import {
  useAccountantApplicationCounts,
  useAccountantApplications,
} from "../../hooks/accountantReceipt.hooks";
import { formatDate } from "../../utils/date";
import { useUser } from "../../contexts/UserContext";

type TAccountantAppRow = {
  id: number;
  applicationId: string;
  plot_owner_s_name?: string;
  status?: string;
  name_of_service?: string;
  submittedAt?: string;
  updatedAt?: string;
  rl_application_type?: string;
  current_phase?: string;
  receipt_no?: string;
  amount_paid?: number | string;
  payment_date?: string;
  payment_status?: string;
  receipt_file?: string;
  totalAmount?: number | string;
};

/**
 * Short labels for the queue's status pills. The stored status strings run to
 * ~50 characters, which wraps inside a rounded pill and throws the row heights
 * out of line with every other column. The full status stays available as the
 * pill's tooltip.
 */
const QUEUE_STATUS_LABELS: Record<string, string> = {
  "Pending Accountant Receipt Upload": "Awaiting Receipt Upload",
  "Returned For Receipt Correction By Level 1 Reviewer":
    "Receipt Correction (L1)",
  "Returned For Receipt Correction By Level 2 Reviewer":
    "Receipt Correction (L2)",
  "Returned For Receipt Correction By Level 3 Reviewer":
    "Receipt Correction (L3)",
};

const AccountantApplications = () => {
  const navigate = useNavigate();
  const user = useUser();

  const isViewOnly = (user?.role || "").toLowerCase() !== "accountant";

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState<"table" | "card">("table");

  // Amount Filter State
  const [amountMatchFilter, setAmountMatchFilter] = useState<"all" | "match" | "mismatch">("all");

  const search = useDebounce(searchQuery);

  const [{ sortKey, sortOrder }, setSort] = useState<{
    sortKey: string | null;
    sortOrder: 1 | -1;
  }>({
    sortKey: null,
    sortOrder: -1,
  });

  // ORIGINAL MULTI-STATUS FILTER PRESERVED FOR AUDIT REFERENCE:
  /*
  const [showPendingAccount, setShowPendingAccount] = useState(true);
  const [showPaymentDiscrepancy, setShowPaymentDiscrepancy] = useState(true);
  const [showReturnedByAccountant, setShowReturnedByAccountant] = useState(false);
  const [showPendingPaymentLevel1, setShowPendingPaymentLevel1] = useState(false);
  */

  // BEFORE — locked to the single status the Accountant originally owned:
  // const activeStatuses = useMemo(() => ["Pending Account Review"], []);

  /**
   * The Accountant now owns three further states beyond the initial review: an
   * accepted applicant receipt awaiting the official receipt upload, and a
   * receipt bounced back for correction by Level 1, Level 2 or Level 3. Leaving them out
   * would make those applications disappear from the queue entirely with nobody
   * able to act on them.
   */
  const activeStatuses = useMemo(
    () => [
      "Pending Account Review",
      "Pending Accountant Receipt Upload",
      "Returned For Receipt Correction By Level 1 Reviewer",
      "Returned For Receipt Correction By Level 2 Reviewer",
      "Returned For Receipt Correction By Level 3 Reviewer",
    ],
    []
  );

  /** Statuses whose next step is the receipt-upload screen, not re-verification. */
  const RECEIPT_UPLOAD_STATUSES = useMemo(
    () => [
      "Pending Accountant Receipt Upload",
      "Returned For Receipt Correction By Level 1 Reviewer",
      "Returned For Receipt Correction By Level 2 Reviewer",
      "Returned For Receipt Correction By Level 3 Reviewer",
    ],
    []
  );

  const { isLoading: isLoadingCount, data: totalCount } =
    useAccountantApplicationCounts({
      search,
      statuses: activeStatuses,
    });

  const { isLoading: isLoadingData, data } = useAccountantApplications({
    page,
    search,
    limit: 10,
    sort: sortKey ?? "",
    sortVal: sortOrder,
    statuses: activeStatuses,
  });

  const filteredData = useMemo(() => {
    let rows = data || [];

    if (amountMatchFilter === "match") {
      rows = rows.filter((r) => {
        const demand = Number(r.totalAmount || 0);
        const paid = Number(r.amount_paid || 0);
        return demand > 0 && Math.abs(demand - paid) < 0.01;
      });
    } else if (amountMatchFilter === "mismatch") {
      rows = rows.filter((r) => {
        const demand = Number(r.totalAmount || 0);
        const paid = Number(r.amount_paid || 0);
        return demand > 0 && Math.abs(demand - paid) >= 0.01;
      });
    }

    return rows;
  }, [data, amountMatchFilter]);

  // MODIFIED IMPLEMENTATION: Increased column minWidths to prevent text ellipsis truncation
  const columns = useMemo(
    (): TableColumn<TAccountantAppRow>[] => [
      {
        name: "Sr. No.",
        minWidth: "120px",
        center: true,
        cell: (_, index) => (page - 1) * 10 + index + 1,
      },
      {
        name: "Application ID",
        sortable: true,
        minWidth: "180px",
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
        cell: (e) => (
          <span
            className="font-semibold text-[#E9B925] cursor-pointer underline"
            onClick={() => {
              navigate(`/accountant-verify-application`, {
                state: { id: e.id, isViewOnly },
              });
            }}
          >
            {e.applicationId}
          </span>
        ),
      },
      {
        name: "Plot Owner's Name",
        sortable: true,
        minWidth: "200px",
        sortField: "plot_owner_s_name",
        selector: (d) => d.plot_owner_s_name || "-",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 1.5,
      },
      {
        name: "Receipt No.",
        minWidth: "220px",
        center: true,
        selector: (d) => d.receipt_no || "-",
        cell: (d) => (
          <span className="font-mono text-sm font-semibold text-gray-700">
            {d.receipt_no || "-"}
          </span>
        ),
      },
      {
        name: "Demand Note Amount",
        minWidth: "220px",
        center: true,
        cell: (d) => {
          const val = Number(d.totalAmount || 0);
          return (
            <span className="font-semibold text-gray-800">
              {val > 0 ? `₹${val.toFixed(2)}` : "-"}
            </span>
          );
        },
      },
      {
        name: "Amount Paid",
        minWidth: "200px",
        center: true,
        cell: (d) => {
          const demand = Number(d.totalAmount || 0);
          const paid = Number(d.amount_paid || 0);
          const isMismatch = demand > 0 && Math.abs(demand - paid) >= 0.01;

          return (
            <span
              className={`font-semibold ${
                isMismatch ? "text-red-600 bg-red-50 px-2 py-1 rounded" : "text-green-700"
              }`}
            >
              {paid > 0 ? `₹${paid.toFixed(2)}` : "-"}
            </span>
          );
        },
      },
      {
        name: "Payment Date",
        center: true,
        minWidth: "160px",
        cell: (d) => (d.payment_date ? formatDate(d.payment_date) : "-"),
      },
      {
        name: "Upload Date",
        center: true,
        minWidth: "160px",
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
      },
      {
        name: "Status",
        minWidth: "280px",
        center: true,
        sortable: true,
        sortField: "status",
        cell: (d) => {
          const isPending =
            d.status === "Pending Account Review" ||
            d.status === "Pending Accountant Receipt Upload";
          /**
           * A reviewer bouncing the official receipt back reads as a fault on
           * this queue the same way an applicant-receipt discrepancy does.
           */
          const isDiscrepancy =
            d.status === "Payment Discrepancy" ||
            d.status === "Returned By Accountant" ||
            d.status === "Returned For Receipt Correction By Level 1 Reviewer" ||
            d.status === "Returned For Receipt Correction By Level 2 Reviewer" ||
            d.status === "Returned For Receipt Correction By Level 3 Reviewer";

          return (
            <span
              title={d.status || ""}
              className={`inline-block whitespace-nowrap text-center px-3 py-1 text-xs font-semibold rounded-full ${
                isPending
                  ? "bg-amber-100 text-amber-800 border border-amber-300"
                  : isDiscrepancy
                  ? "bg-red-100 text-red-800 border border-red-300"
                  : "bg-green-100 text-green-800 border border-green-300"
              }`}
            >
              {QUEUE_STATUS_LABELS[d.status || ""] || d.status || "-"}
            </span>
          );
        },
      },
      {
        name: "Action",
        center: true,
        minWidth: "120px",
        cell: (e) => (
          <div className="flex justify-center">
            <ViewButton
              onClick={() => {
                /**
                 * An application past the applicant-receipt check does not need
                 * re-verifying — it needs the official receipt issued (or
                 * re-issued), so send it straight to the upload screen.
                 */
                const target =
                  !isViewOnly && RECEIPT_UPLOAD_STATUSES.includes(e.status || "")
                    ? "/accountant-receipt-upload"
                    : "/accountant-verify-application";

                navigate(target, {
                  state: { id: e.id, isViewOnly },
                });
              }}
            />
          </div>
        ),
      },
    ],
    [navigate, page, isViewOnly, RECEIPT_UPLOAD_STATUSES]
  );

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row justify-between mb-4 gap-4 items-start md:items-center">
        <div>
          <h4 className="text-2xl text-gray-800 font-bold">
            Receipts Verification
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Reconcile applicant payment receipts against bank statements.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center grow md:grow-0 w-full md:w-auto">
          {/* Search Bar */}
          <div className="relative flex-grow sm:w-64">
            <input
              type="text"
              placeholder="Search Application ID, Owner, Receipt..."
              value={searchQuery}
              onChange={(e) => {
                setPage(1);
                setSearchQuery(e.target.value);
              }}
              className="w-full pl-9 pr-4 py-2 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <i className="fa fa-search absolute left-3 top-3 text-gray-400 text-sm"></i>
          </div>

          {/* Amount Match Dropdown Filter */}
          <select
            value={amountMatchFilter}
            onChange={(e: any) => setAmountMatchFilter(e.target.value)}
            className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Amounts</option>
            <option value="match">Exact Match (Paid = Demand)</option>
            <option value="mismatch">Mismatch (Paid ≠ Demand)</option>
          </select>

          {/* View Mode Toggle */}
          <button
            className="p-2.5 bg-white border border-gray-300 rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
            title={view === "card" ? "Switch to Table View" : "Switch to Card View"}
          >
            <i
              className={`fa ${
                view === "card" ? "fa-th-large" : "fa-list-ul"
              } text-primary text-lg`}
            />
          </button>
        </div>
      </div>

      {/* ORIGINAL STATUS CHECKBOXES FILTER CONTAINER PRESERVED FOR AUDIT REFERENCE:
      <div className="flex flex-wrap items-center gap-4 bg-white border border-gray-200 rounded-xl p-3 mb-6 text-sm text-gray-700 shadow-sm">
        <span className="font-semibold text-gray-500">Status Filter:</span>
        <label className="flex items-center space-x-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={true}
            disabled
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
          />
          <span className="font-medium">Pending Account Review</span>
        </label>
      </div>
      */}

      {/* Content Rendering */}
      {view === "table" ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={isLoadingData}
            sortIcon={<SortIcon />}
            customStyles={tableCustomStyles}
            onSort={(column, sortDirection) => {
              if (column.sortField) {
                setSort({
                  sortKey: column.sortField,
                  sortOrder: sortDirection === "asc" ? 1 : -1,
                });
              }
            }}
            noDataComponent={
              <div className="p-8 text-center text-gray-500 font-medium">
                No payment receipts found matching the selected criteria.
              </div>
            }
          />
          <Paginator
            page={page}
            count={totalCount || 0}
            limit={10}
            setPage={(p: number) => setPage(p)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredData.map((item) => (
            <BoxComponent key={item.id}>
              <div
                onClick={() => {
                  navigate(`/accountant-verify-application`, {
                    state: { id: item.id, isViewOnly },
                  });
                }}
                className="cursor-pointer space-y-2"
              >
                <div className="flex justify-between items-center">
                  <span className="font-bold text-primary">{item.applicationId}</span>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold">
                    {item.status}
                  </span>
                </div>
                <div className="text-sm font-semibold text-gray-800">
                  {item.plot_owner_s_name || "-"}
                </div>
                <div className="text-xs text-gray-500">
                  Service: {(item as any).name_of_service || item.rl_application_type || "-"}
                </div>
                <div className="text-xs text-gray-500">
                  Submitted: {item.submittedAt ? formatDate(item.submittedAt) : "-"}
                </div>
              </div>
            </BoxComponent>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountantApplications;
